import { motion } from "framer-motion";
import { MessageCircle, Instagram, Mail, ArrowUpRight } from "lucide-react";
import profile from "@/data/profile";
import { trackOutbound } from "@/analytics/ga4";
import SectionGlow from "@/components/site/SectionGlow";

const BehanceIcon = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
        <path d="M22 7h-7v-2h7v2zm1.726 10c-.442 1.297-2.029 3-5.101 3-3.074 0-5.564-1.729-5.564-5.675 0-3.91 2.325-5.92 5.466-5.92 3.082 0 4.964 1.782 5.375 4.426.078.506.109 1.188.095 2.14h-8.027c.13 3.211 3.483 3.312 4.588 2.029h3.168zm-7.686-4h4.965c-.105-1.547-1.136-2.219-2.477-2.219-1.466 0-2.277.768-2.488 2.219zm-9.574 6.988h-6.466v-15.973h6.953c5.476.081 5.58 5.444 2.72 6.906 3.461 1.26 3.577 8.475-3.207 9.067zm-3.466-9.06h3.13c2.385 0 2.5-3.39 0-3.39h-3.13v3.39zm0 6.072h3.179c2.687 0 2.928-3.604 0-3.604h-3.179v3.604z" />
    </svg>
);

// Each channel carries its own brand-colour gradient so the row reads as a set
// of real, recognisable buttons on the dark canvas.
const BUTTONS = [
    {
        id: "whatsapp",
        label: "WhatsApp",
        sub: "Fastest reply · talk to Pranto directly",
        href: profile.socials.whatsapp,
        Icon: MessageCircle,
        grad: "linear-gradient(135deg, #25D366 0%, #0B7A54 100%)",
        glow: "rgba(37, 211, 102, 0.45)",
    },
    {
        id: "instagram",
        label: "Instagram",
        sub: "@pranto.gfx · latest work",
        href: profile.socials.instagram,
        Icon: Instagram,
        grad: "linear-gradient(135deg, #833AB4 0%, #C13584 52%, #5B51D8 100%)",
        glow: "rgba(193, 53, 132, 0.45)",
    },
    {
        id: "behance",
        label: "Behance",
        sub: "be.net/shaikatpranto · full case studies",
        href: profile.socials.behance,
        Icon: BehanceIcon,
        grad: "linear-gradient(135deg, #1769FF 0%, #0A3AB5 100%)",
        glow: "rgba(23, 105, 255, 0.45)",
    },
    {
        id: "email",
        label: "Email",
        sub: profile.email,
        href: `mailto:${profile.email}`,
        Icon: Mail,
        grad: "linear-gradient(135deg, #EA4335 0%, #B31412 100%)",
        glow: "rgba(234, 67, 53, 0.45)",
    },
];

export const Contact = ({ onStartProject }) => {
    return (
        <section id="contact" className="pg-section" aria-label="Contact" data-testid="contact-section">
            <SectionGlow tone="warm" />
            <div className="pg-container">
                <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
                    {/* Left: pitch */}
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-5"
                    >
                        <h2
                            className="pg-h2-section"
                            style={{ color: "var(--pg-fg)" }}
                        >
                            Let&rsquo;s start the
                            {" "}<span className="pg-em-italic">conversation</span>.
                        </h2>
                        <p className="pg-lede mt-5 max-w-md">
                            Send a short brief, share what you&rsquo;re building, and let&rsquo;s find the right
                            engagement, from a focused logo system to a full brand ecosystem.
                        </p>
                        <button
                            type="button"
                            onClick={() => onStartProject?.()}
                            data-testid="contact-start-project-button"
                            className="pg-btn-ghost pg-btn-glass-lume mt-8 inline-flex"
                        >
                            <span>Start A Project</span>
                            <ArrowUpRight className="h-4 w-4" strokeWidth={1.6} />
                        </button>
                    </motion.div>

                    {/* Right: glass conversion module */}
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-7"
                    >
                        <div className="pg-glass rounded-3xl p-5 sm:p-7" data-testid="contact-module">
                            <div className="mb-5 flex items-center justify-between gap-3">
                                <span className="font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>
                                    Reach the studio
                                </span>
                                <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--pg-fg-muted)" }}>
                                    <motion.span
                                        aria-hidden
                                        className="inline-block h-1.5 w-1.5 rounded-full"
                                        style={{ background: "var(--pg-accent)" }}
                                        animate={{ opacity: [0.4, 1, 0.4] }}
                                        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                                    />
                                    Replies within a day
                                </span>
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {BUTTONS.map(({ id, label, sub, href, Icon, grad, glow }) => (
                                    <a
                                        key={id}
                                        href={href}
                                        target={href.startsWith("mailto") ? "_self" : "_blank"}
                                        rel="noopener noreferrer"
                                        onClick={() => trackOutbound(id)}
                                        data-testid={`contact-${id}-button`}
                                        className="pg-brand-tile group relative flex items-start justify-between gap-4 overflow-hidden rounded-2xl p-5"
                                        style={{
                                            background: grad,
                                            "--brand-glow": glow,
                                        }}
                                    >
                                        <div className="relative z-[1]">
                                            <span
                                                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl"
                                                style={{
                                                    background: "rgba(255, 255, 255, 0.18)",
                                                    color: "#FFFFFF",
                                                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.35)",
                                                }}
                                            >
                                                <Icon className="h-5 w-5" strokeWidth={1.7} />
                                            </span>
                                            <div className="mt-4">
                                                <div className="font-display text-lg" style={{ color: "#FFFFFF" }}>
                                                    {label}
                                                </div>
                                                <div className="mt-1.5 text-sm" style={{ color: "rgba(255,255,255,0.86)" }}>
                                                    {sub}
                                                </div>
                                            </div>
                                        </div>
                                        <ArrowUpRight
                                            className="relative z-[1] mt-1 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                                            style={{ color: "#FFFFFF" }}
                                            strokeWidth={1.8}
                                        />
                                    </a>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Contact;
